import type { BudgetMember, Category, Subcategory, Transaction, TransactionType } from '../../domain/model';
import { todayIso, type Store } from '../../data/store/store';

/**
 * The Quick Add grid (brief §8): the thirteen things people pay most,
 * each mapped onto the default category tree, followed by the user's own
 * categories. `label` overrides the plain default name where the brief uses
 * a shorter word ("Gas", "Loan"); a name the user gave always wins.
 */
interface QuickSpec {
  id: string;
  category: string;
  sub: string | null;
  label?: string;
}

const QUICK: QuickSpec[] = [
  { id: 'rent', category: 'housing', sub: 'rent' },
  { id: 'electricity', category: 'utilities', sub: 'electricity' },
  { id: 'gas', category: 'utilities', sub: 'natural_gas', label: 'quickAdd.gas' },
  { id: 'water', category: 'utilities', sub: 'water' },
  { id: 'internet', category: 'utilities', sub: 'internet' },
  { id: 'mobile', category: 'utilities', sub: 'mobile' },
  { id: 'groceries', category: 'food', sub: 'groceries' },
  { id: 'entertainment', category: 'entertainment', sub: null },
  { id: 'shopping', category: 'shopping', sub: null },
  { id: 'transport', category: 'transportation', sub: null, label: 'quickAdd.transport' },
  { id: 'loan', category: 'obligations', sub: 'personal_loan', label: 'quickAdd.loan' },
  { id: 'card', category: 'obligations', sub: 'credit_card_bill', label: 'quickAdd.creditCard' },
  { id: 'other', category: 'other', sub: null },
];

const DEBT = new Set(['personal_loan', 'car_loan', 'mortgage_loan', 'student_loan', 'installment', 'overdraft', 'other_debt']);

/** What kind of entry a pick makes (brief §15): loans are debt payments, a card bill is a card payment. */
export function typeFor(sub: Pick<Subcategory, 'systemKey'> | null): TransactionType {
  if (sub?.systemKey === 'credit_card_bill') return 'credit_card_payment';
  if (sub?.systemKey && DEBT.has(sub.systemKey)) return 'debt_payment';
  return 'expense';
}

export interface QuickPick {
  id: string;
  categoryId: string;
  subcategoryId: string | null;
  /** Locale key (categories.x / quickAdd.x), used when there is no user name. */
  labelKey?: string;
  name?: string;
  colorSlot: number;
  unit: string | null;
  type: TransactionType;
}

function pickFor(category: Category, sub: Subcategory | null, id: string, labelKey?: string): QuickPick {
  // Named by the subcategory when there is one, else by the category; a user-given name wins.
  const target = sub ?? category;
  return {
    id,
    categoryId: category.id,
    subcategoryId: sub?.id ?? null,
    labelKey: target.name ? undefined : (labelKey ?? (target.systemKey ? `categories.${target.systemKey}` : undefined)),
    name: target.name ?? undefined,
    colorSlot: category.colorSlot,
    unit: sub?.defaultUnit ?? null,
    type: typeFor(sub),
  };
}

/**
 * Resolve the quick grid against a budget's active categories (archived
 * ones drop out), then append the user's own categories and subcategories,
 * most-used first once there is history.
 */
export async function loadQuickPicks(store: Store, spaceId: string, usage: Map<string, number>): Promise<QuickPick[]> {
  const cats = await store.categories.listForSpace(spaceId);
  const subs = (await store.subcategories.list({ spaceId })).filter((s) => !s.archivedAt);
  const active = new Map(cats.map((c) => [c.id, c]));
  const byKey = new Map(cats.filter((c) => c.systemKey).map((c) => [c.systemKey!, c]));
  const picks: QuickPick[] = [];
  for (const q of QUICK) {
    const c = byKey.get(q.category);
    if (!c) continue;
    const s = q.sub ? subs.find((x) => x.categoryId === c.id && x.systemKey === q.sub) : null;
    if (q.sub && !s) continue;
    picks.push(pickFor(c, s ?? null, q.id, q.label));
  }
  for (const c of cats.filter((x) => x.systemKey === null)) picks.push(pickFor(c, null, c.id));
  for (const s of subs.filter((x) => x.systemKey === null && active.has(x.categoryId))) picks.push(pickFor(active.get(s.categoryId)!, s, s.id));

  const used = (p: QuickPick) => usage.get(`${p.categoryId}/${p.subcategoryId ?? ''}`) ?? 0;
  return picks
    .map((p, i) => ({ p, i, n: used(p) }))
    .sort((a, b) => b.n - a.n || a.i - b.i)
    .map((x) => x.p);
}

/** Every active category and its active subcategories, for "All categories". */
export async function loadAllPicks(store: Store, spaceId: string): Promise<{ category: QuickPick; subs: QuickPick[] }[]> {
  const cats = await store.categories.listForSpace(spaceId);
  const subs = (await store.subcategories.list({ spaceId }, { orderBy: 'sort_order, created_at' })).filter((s) => !s.archivedAt);
  return cats.map((c) => ({
    category: pickFor(c, null, c.id),
    subs: subs.filter((s) => s.categoryId === c.id).map((s) => pickFor(c, s, s.id)),
  }));
}

/* ------------------------------------------------------------------ warm start */

export interface QuickAddBasics {
  picks: QuickPick[];
  members: BudgetMember[];
  me: BudgetMember | null;
  /** The last entry per pick (`categoryId/subcategoryId`), for "Same as last time" on the first frame. */
  last: Map<string, Transaction>;
}

export const pickKey = (p: Pick<QuickPick, 'categoryId' | 'subcategoryId'>) => `${p.categoryId}/${p.subcategoryId ?? ''}`;

/**
 * What the sheet needs before its first frame, per budget. The app shell
 * refreshes it whenever data changes, so + opens on a full grid instead of
 * waiting on four queries (≈ half a second through the native bridge).
 */
const warm = new Map<string, QuickAddBasics>();

export const peekQuickAddBasics = (spaceId: string) => warm.get(spaceId);

export async function loadQuickAddBasics(store: Store, spaceId: string, userId: string): Promise<QuickAddBasics> {
  const usage = await store.transactions.usageSince(spaceId, todayIso(Date.now() - 90 * 86_400_000));
  const [picks, members, mine, last] = await Promise.all([
    loadQuickPicks(store, spaceId, usage),
    store.members.list({ spaceId }, { orderBy: 'created_at' }),
    store.members.forUser(spaceId, userId),
    store.transactions.latestPerPick(spaceId),
  ]);
  const basics = { picks, members, me: mine ?? members[0] ?? null, last };
  warm.set(spaceId, basics);
  return basics;
}
